// 2. External libraries
import { redirect } from '@tanstack/react-router';

// 4. Type imports
import type { RouterContext } from '~/router';
import type { ListTopicsResponse } from '~/generated/api/model';

// 5. Custom hooks and utilities
import { listTopicsV1TopicsGet } from '~/generated/api/topics/topics';
import { queryKeys } from '~/hooks/queryKeys';

// Page 0, pageSize 1: minimal payload needed to read the total topic count.
const TOPICS_PROBE_PAGE = 0;
const TOPICS_PROBE_PAGE_SIZE = 1;

/**
 * Route guard for the authenticated layout route. Runs before the route loads
 * and redirects users into onboarding when they have not finished (or skipped)
 * it.
 *
 * Redirect logic:
 * - In-progress onboarding (currentStep > 0, not complete/skipped) → /onboarding
 * - Not complete AND not skipped AND zero topics → /onboarding
 * - Complete OR skipped OR has topics → allow the route to load
 *
 * Exemption is structural: only the authenticated layout route carries this
 * guard, so sign-in, callback, and onboarding routes never run it.
 *
 * Telegram OAuth can land mid-flow with topics already created, so the
 * currentStep check keeps an in-progress user inside onboarding even when they
 * have topics.
 *
 * Reads `auth` and `queryClient` from the router context and fetches the topic
 * count through the same query key as the topics list hook so the guard shares
 * the cache rather than issuing a redundant request.
 *
 * While Clerk is still loading (isLoaded false, user null), the guard resolves
 * without fetching or redirecting, so the loading window is not mistaken for a
 * user who still needs onboarding.
 */
export async function onboardingBeforeLoad({ context }: { context: RouterContext }): Promise<void> {
  const { auth, queryClient } = context;

  // Auth has not resolved yet: defer the onboarding decision until it does.
  if (!auth.isLoaded) {
    return;
  }

  const topicsData = await queryClient.ensureQueryData<ListTopicsResponse>({
    queryKey: queryKeys.topics.list(TOPICS_PROBE_PAGE, TOPICS_PROBE_PAGE_SIZE),
    queryFn: () =>
      listTopicsV1TopicsGet({ page: TOPICS_PROBE_PAGE, pageSize: TOPICS_PROBE_PAGE_SIZE }),
  });

  const user = auth.user;
  const onboardingComplete = user?.publicMetadata?.onboardingComplete === true;
  const onboardingSkipped = user?.publicMetadata?.onboardingSkipped === true;
  const hasTopics = (topicsData?.total ?? 0) > 0;
  const onboardingProgress = user?.publicMetadata?.onboardingProgress;
  const hasInProgressOnboarding =
    onboardingProgress !== undefined &&
    onboardingProgress.currentStep > 0 &&
    !onboardingComplete &&
    !onboardingSkipped;

  const needsOnboarding =
    hasInProgressOnboarding || (!onboardingComplete && !onboardingSkipped && !hasTopics);

  if (needsOnboarding) {
    // oxlint-disable-next-line typescript/only-throw-error -- TanStack Router redirect() is thrown by design
    throw redirect({ to: '/onboarding', replace: true });
  }
}
